import { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import {
  auditLogApi,
  usersApi,
  AuditLogEntry,
  AuditLogPagination,
  ApiRequestError,
} from '@/lib/api';
import type { PublicUser } from '@apartment-booking/shared';

const ACTION_LABELS: Record<string, string> = {
  'user.created': 'User Created',
  'user.updated': 'User Updated',
  'user.deactivated': 'User Deactivated',
  'user.login': 'User Login',
  'user.logout': 'User Logout',
  'booking.requested': 'Booking Requested',
  'booking.approved': 'Booking Approved',
  'booking.declined': 'Booking Declined',
  'booking.cancelled': 'Booking Cancelled',
  'booking.modified': 'Booking Modified',
  'booking.deleted': 'Booking Deleted',
  'settings.updated': 'Settings Updated',
  'property.updated': 'Property Updated',
  'calendar.imported': 'Calendar Imported',
  'guide.created': 'Guide Created',
  'guide.updated': 'Guide Updated',
  'guide.deleted': 'Guide Deleted',
  'document.uploaded': 'Document Uploaded',
  'document.deleted': 'Document Deleted',
  'invitation.sent': 'Invitation Sent',
  'invitation.accepted': 'Invitation Accepted',
  'password.reset_requested': 'Password Reset Requested',
  'password.reset_completed': 'Password Reset Completed',
  'reminder.sent': 'Reminder Sent',
  'reminder.unsubscribed': 'Reminder Unsubscribed',
};

const UNIQUE_ACTIONS = Object.keys(ACTION_LABELS);

export default function AuditLogPage() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [pagination, setPagination] = useState<AuditLogPagination | null>(null);
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await auditLogApi.getEntries({
        page,
        limit: 25,
        action: actionFilter || undefined,
        user_id: userFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });
      setEntries(result.entries);
      setPagination(result.pagination);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to load audit log');
      }
    } finally {
      setIsLoading(false);
    }
  }, [page, actionFilter, userFilter, startDate, endDate]);

  const fetchUsers = useCallback(async () => {
    try {
      const result = await usersApi.list();
      setUsers(result.users);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleExport = () => {
    const url = auditLogApi.getExportUrl({
      action: actionFilter || undefined,
      user_id: userFilter || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    });
    window.open(url, '_blank');
  };

  const clearFilters = () => {
    setActionFilter('');
    setUserFilter('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const formatDetails = (details: string | null): string => {
    if (!details) return '';
    try {
      const parsed = JSON.parse(details);
      return Object.entries(parsed)
        .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
        .join(', ');
    } catch {
      return details;
    }
  };

  const getActionBadgeColor = (action: string) => {
    if (action.includes('created') || action.includes('approved') || action.includes('accepted')) {
      return 'bg-green-100 text-green-800';
    }
    if (action.includes('deleted') || action.includes('declined') || action.includes('deactivated')) {
      return 'bg-red-100 text-red-800';
    }
    if (action.includes('updated') || action.includes('modified')) {
      return 'bg-blue-100 text-blue-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and export system activity. Logs are retained for 12 months.
          </p>
        </div>
        <button onClick={handleExport} className="btn-secondary">
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label htmlFor="action" className="label">
              Action
            </label>
            <select
              id="action"
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(1);
              }}
              className="input"
            >
              <option value="">All Actions</option>
              {UNIQUE_ACTIONS.map((action) => (
                <option key={action} value={action}>
                  {ACTION_LABELS[action] || action}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="user" className="label">
              User
            </label>
            <select
              id="user"
              value={userFilter}
              onChange={(e) => {
                setUserFilter(e.target.value);
                setPage(1);
              }}
              className="input"
            >
              <option value="">All Users</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="startDate" className="label">
              From Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              className="input"
            />
          </div>

          <div>
            <label htmlFor="endDate" className="label">
              To Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              className="input"
            />
          </div>

          <div className="flex items-end">
            <button onClick={clearFilters} className="btn-secondary w-full">
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    </div>
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No audit log entries found.
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {format(parseISO(entry.created_at), 'MMM d, yyyy h:mm a')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {entry.user_name || 'System'}
                      </div>
                      {entry.user_email && (
                        <div className="text-xs text-gray-500">{entry.user_email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionBadgeColor(
                          entry.action
                        )}`}
                      >
                        {ACTION_LABELS[entry.action] || entry.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {entry.target_type}
                      {entry.target_id && (
                        <span className="text-gray-400"> #{entry.target_id.slice(0, 8)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                      {formatDetails(entry.details)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing{' '}
              <span className="font-medium">
                {(pagination.page - 1) * pagination.limit + 1}
              </span>{' '}
              to{' '}
              <span className="font-medium">
                {Math.min(pagination.page * pagination.limit, pagination.total)}
              </span>{' '}
              of <span className="font-medium">{pagination.total}</span> entries
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={page === pagination.totalPages}
                className="btn-secondary disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
